import Button from 'components/Button';
import Error from 'components/form/Error';
import ActionModal from 'components/Modal/ActionModal';
import Space from 'components/Space';
import { FC, useContext, useState } from 'react';
import { mutate } from 'swr';
import privateAPI from 'utils/privateAxiosInstance';
import { getLocaleDate, getLocaleTime } from 'utils/time';
import { ReviewPageContext } from '.';
import { ButtonsContainer, Container } from './components';
import NoAnswer from './NoAnswer';

const WaitingMentee: FC = () => {
  const { interview } = useContext(ReviewPageContext);

  const dateDifference = interview?.date
    ? new Date().getTime() - new Date(interview?.date).getTime()
    : undefined;

  return dateDifference && dateDifference < 0 ? (
    <WaitingMenteeBeforeDate />
  ) : (
    <NoAnswer />
  );
};

const typeLabels: Record<string, string> = {
  PHONE: 'par téléphone',
  MEETJIT: 'en visio',
  IRL: 'en personne',
  OTHER: 'via un autre moyen'
};

const WaitingMenteeBeforeDate: FC = () => {
  const { interview } = useContext(ReviewPageContext);
  const currentMentorId = interview?.mentorship?.mentorId;
  const isAuthor = String(interview?.authorId) === String(currentMentorId);
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  // Fonction pour ANNULER (Cas où le mentor est l'auteur)
  const handleCancel = () => {
    setLoading(true);
    setError(false);
    privateAPI
      .post(`/interview/mentor-cancel/${interview?.id}`)
      .then(() => {
        mutate(`/interviews/${interview?.id}`);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  // Fonction pour ACCEPTER (Cas où le mentoré est l'auteur)
  const handleAccept = () => {
    setLoading(true);
    setError(false);
    privateAPI
      .post(`/interview/mentor-accept/${interview?.id}`)
      .then(() => {
        mutate(`/interviews/${interview?.id}`);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  // Fonction pour REFUSER (Cas où le mentoré est l'auteur)
  const handleRefuse = () => {
    setLoading(true);
    setError(false);
    privateAPI
      .post(`/interview/mentor-refuse/${interview?.id}`)
      .then(() => {
        mutate(`/interviews/${interview?.id}`);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  // --- CAS 1 : LE MENTOR A PROPOSÉ ---
  if (isAuthor) {
     return (
      <>
        <ActionModal
          open={cancelOpen}
          close={() => setCancelOpen(false)}
          title="Annuler l'entretien"
          applyText="Annuler l'entretien"
          apply={handleCancel}
          cancelText="Retour"
          color="error"
          loading={loading}
        >
          <p>
            Vous avez proposé un entretien à{' '}
            {interview!.mentorship?.mentee?.firstName} le{' '}
            {interview!.date && getLocaleDate(interview!.date)} à{' '}
            {interview?.date && getLocaleTime(interview.date)}.
          </p>
          <Space height={1} />
          <p>Cette action est irréversible. Souhaitez-vous vraiment l'annuler ?</p>
        </ActionModal>
        <Container>
          <p>
            Vous avez proposé un entretien à{' '}
            {interview!.mentorship?.mentee?.firstName} le{' '}
            {interview!.date && getLocaleDate(interview!.date)} à{' '}
            {interview?.date && getLocaleTime(interview.date)}.
          </p>
          <p>{interview!.mentorship?.mentee?.firstName} doit maintenant accepter ou refuser.</p>
          <Space height={1} />
          <ButtonsContainer>
            <Button variant="outlined" color="primary" onClick={() => setCancelOpen(true)}>
              Annuler l'entretien
            </Button>
          </ButtonsContainer>
          {error && <Error>Une erreur est survenue lors de l'annulation.</Error>}
        </Container>
      </>
    );
  }

  // --- CAS 2 : LE MENTORÉ A PROPOSÉ ---
  return (
    <Container>
      <p>
        {interview!.mentorship?.mentee?.firstName} vous a proposé un entretien{' '}
        <strong>{typeLabels[interview?.type || ''] || "à distance"}</strong> le{' '}
        {interview!.date && getLocaleDate(interview!.date)} à{' '}
        {interview?.date && getLocaleTime(interview.date)}.
      </p>
      <p>Souhaitez-vous accepter cette proposition d'entretien ?</p>
      <Space height={1} />
      <ButtonsContainer>
        <Button 
          color="primary" 
          onClick={handleAccept} 
          disabled={loading}
        >
          {loading ? 'Chargement...' : "Accepter l'entretien"}
        </Button>
        <Space width={1} />
        <Button 
          variant="outlined" 
          color="error" 
          onClick={handleRefuse} 
          disabled={loading}
        >
          Refuser
        </Button>
      </ButtonsContainer>
      {error && <Error>Une erreur est survenue. Veuillez réessayer.</Error>}
    </Container>
  );
};

export default WaitingMentee;